<template>
  <div>
    <z-link tag="router-link" :to="{ name: 'search-student', params: { encryptedInstitutionId } }">
      Ir para a busca de alunos
    </z-link>

    <template v-if="studentBillings">
      <z-card class="student-details__student-info">
        <div>
          <z-text size="medium" tag="span" weight="bold">
            Aluno:
          </z-text>
          <z-text size="medium" tag="span">
            {{ studentBillings.student.name }}
          </z-text>
        </div>

        <div>
          <z-text size="medium" tag="span" weight="bold">
            CPF:
          </z-text>
          <z-text size="medium" tag="span">
            {{ studentBillings.student.cpf }}
          </z-text>
        </div>
      </z-card>

      <z-card v-if="emptyBillings">
        <z-text size="medium" tag="span">
          Nenhuma mensalidade encontrada
        </z-text>
      </z-card>

      <z-card v-for="(billing, billingIndex) in studentBillings.billings" :key="billingIndex">
        <div>
          <z-text size="medium" tag="span" weight="bold">
            Curso:
          </z-text>
          <z-text size="medium" tag="span">
            {{ billing.course_name }}
          </z-text>
        </div>

        <z-card
          class="student-details__bill-info"
          v-for="(bill, billIndex) in billing.bills"
          :key="`${billingIndex}bills${billIndex}`"
        >
          <div>
            <z-text tag="div" size="medium" weight="bold">
              {{ bill.month_name }}/{{ bill.bill_year }}
            </z-text>
            <z-badge-text :variant="colorByStatus(bill.status)" :value="bill.translated_status" />
          </div>

          <div>
            <div>
              <z-text tag="span" weight="bold" size="medium">
                Valor da mensalidade:
              </z-text>
              <z-text tag="span" size="medium">
                {{ bill.bill_value | formatCurrency }}
              </z-text>
            </div>

            <div>
              <z-text tag="span" weight="bold" size="medium" v-if="bill.status === 'pending'">
                Valor da multa:
              </z-text>
              <z-text tag="span" size="medium">
                {{ bill.penalty | formatCurrency }}
              </z-text>
            </div>

            <div>
              <z-text tag="span" weight="bold" size="medium">
                Valor do juros:
              </z-text>
              <z-text tag="span" size="medium">
                {{ bill.interest | formatCurrency }}
              </z-text>
            </div>
          </div>

          <z-button
            class="bill-info__button"
            :is-loading="bill.loadingBoletoBillPayment"
            @click="downloadBoleto(bill)"
          >
            {{ billPaymentActionText(bill) }}
          </z-button>
        </z-card>
      </z-card>
    </template>
  </div>
</template>

<script>
import InstitutionPanelService from 'services/InstitutionPanelService';

export default {
  props: {
    encryptedInstitutionId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      studentBillings: null,
    };
  },
  computed: {
    emptyBillings() {
      return !this.studentBillings.billings.length;
    },
  },
  beforeMount() {
    const { params } = this.$route;

    if (!params.studentBillings) {
      return this.searchStudent(params);
    }

    this.studentBillings = params.studentBillings;

    return true;
  },
  methods: {
    searchStudent(params) {
      this.$pageLoader.show();

      InstitutionPanelService.loadStudentBillings(this.encryptedInstitutionId, null, params.cpfHash)
        .then((data) => {
          this.studentBillings = data;
        })
        .catch(() => {
          this.$toast.show('danger', 'Erro ao buscar aluno', 4000);
          this.$router.push({ name: 'landing' });
        })
        .finally(() => {
          this.$pageLoader.hide();
        });
    },
    colorByStatus(status) {
      if (status === 'pending') {
        return 'danger';
      }

      if (status === 'paid') {
        return 'success';
      }

      return 'info';
    },
    billPaymentActionText(bill) {
      if (this.isBoletoBillPaymentGenerated(bill)) {
        return 'Baixar boleto';
      }

      return 'Gerar boleto';
    },
    isBoletoBillPaymentGenerated(bill) {
      return bill.boleto_bill_payment && bill.boleto_bill_payment.url;
    },
    downloadBoleto(bill) {
      if (bill.loadingBoletoBillPayment) {
        return;
      }

      this.$set(bill, 'loadingBoletoBillPayment', true);

      if (!this.isBoletoBillPaymentGenerated(bill)) {
        this.generateBoletoBillPayment(bill);

        return;
      }

      InstitutionPanelService.downloadBoleto(this.encryptedInstitutionId, bill.boleto_bill_payment.id)
        .then((blob) => {
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.target = '_blank';
          link.click();
        })
        .catch(() => {
          this.$toast.show('danger', 'Erro ao baixar boleto, tente novamente mais tarde', 4000);
        })
        .finally(() => {
          this.$set(bill, 'loadingBoletoBillPayment', false);
        });
    },
    generateBoletoBillPayment(bill) {
      return InstitutionPanelService.createBoletoBillPayment(this.encryptedInstitutionId, bill.id)
        .then((data) => {
          this.$set(bill, 'boleto_bill_payment', { id: data.bill_payment_id });

          return this.checkBoletoStatus(bill);
        })
        .catch(() => {
          this.$toast.show('danger', 'Erro ao baixar boleto, tente novamente mais tarde', 4000);
          this.$set(bill, 'loadingBoletoBillPayment', false);
        });
    },
    checkBoletoStatus(bill) {
      const billPaymentId = bill.boleto_bill_payment.id;

      return InstitutionPanelService.checkBoletoBillPaymentStatus(this.encryptedInstitutionId, billPaymentId)
        .then((data) => {
          if (data.url) {
            this.$set(bill.boleto_bill_payment, 'url', data.url);
            this.$set(bill, 'loadingBoletoBillPayment', false);

            return true;
          }

          return setTimeout(() => this.checkBoletoStatus(bill), 2000);
        })
        .catch(() => {
          this.$set(bill, 'loadingBoletoBillPayment', false);
          this.$toast.show('danger', 'Erro ao baixar boleto, tente novamente mais tarde', 4000);

          return false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.student-details__student-info {
  margin-bottom: var(--space-large);
  margin-top: var(--space-jumbo);
}

.student-details__bill-info {
  align-items: center;
  display: grid;
  grid-column-gap: var(--space-medium);
  grid-template-columns: 1fr 1fr 1fr;
  justify-items: flex-start;
  margin-top: var(--space-medium);

  @media screen and (max-width: $screen-md-max){
    grid-template-columns: 1fr;
  }
}

.bill-info__button {
  justify-self: flex-end;
}
</style>
